import { useState, useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import {
    Upload,
    X,
    FileText,
    Image,
    Loader2,
    Check,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button-new";
import { documentApiService } from "@/features/documents/services/documentApiService";
import { S3Upload } from "@/features/documents/services/uploadService";
import { cn } from "@/lib/utils";

interface DocumentUploaderProps {
    onClose?: () => void;
    className?: string;
    elevation?: 0 | 1 | 2 | 3 | 4 | 5;
}

/**
 * File upload status types
 */
type FileStatus = "idle" | "uploading" | "success" | "error";

/**
 * File with upload status tracking
 */
interface FileWithStatus {
    /** The actual file object */
    file: File;
    /** Unique identifier for tracking */
    id: string;
    /** Current status of the file */
    status: FileStatus;
    /** Error message if upload failed */
    error?: string;
}

export function DocumentUploader({
    onClose,
    className,
    elevation = 1,
}: DocumentUploaderProps = {}) {
    // State for managing uploaded files
    const [files, setFiles] = useState<FileWithStatus[]>([]);

    // Stats about current files
    const fileStats = useMemo(() => {
        const total = files.length;
        const pending = files.filter((f) => f.status === "idle").length;
        const uploading = files.filter((f) => f.status === "uploading").length;
        const completed = files.filter((f) => f.status === "success").length;
        const failed = files.filter((f) => f.status === "error").length;
        const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);

        return { total, pending, uploading, completed, failed, totalSize };
    }, [files]);

    /**
     * Format file size in human-readable format
     */
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    /**
     * Check if a file is a duplicate of an existing file
     */
    const isDuplicateFile = (
        file: File,
        existingFiles: FileWithStatus[],
    ): boolean => {
        return existingFiles.some((existingFile) => {
            const existingF = existingFile.file;
            // Compare name, size, and last modified date to identify duplicates
            return (
                existingF.name === file.name &&
                existingF.size === file.size &&
                existingF.lastModified === file.lastModified
            );
        });
    };

    /**
     * Handle dropped or selected files
     */
    const onDrop = useCallback((acceptedFiles: File[]) => {
        setFiles((prev) => {
            // Filter out duplicates
            const uniqueFiles = acceptedFiles.filter(
                (file) => !isDuplicateFile(file, prev),
            );

            if (uniqueFiles.length < acceptedFiles.length) {
                const duplicateCount =
                    acceptedFiles.length - uniqueFiles.length;
                toast.info(
                    `${duplicateCount} duplicate file${duplicateCount > 1 ? "s" : ""} skipped`,
                );
            }

            // Create tracking objects for unique files
            const newFiles = uniqueFiles.map((file) => ({
                file,
                id: crypto.randomUUID(),
                status: "idle" as FileStatus,
            }));

            return [...prev, ...newFiles];
        });
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/jpeg": [],
            "image/jpg": [],
            "application/pdf": [],
        },
        maxSize: 10485760, // 10MB
        multiple: true,
        preventDropOnDocument: true, // Prevent browser from opening files
    });

    const removeFile = (id: string) => {
        setFiles((files) => files.filter((file) => file.id !== id));
    };

    /**
     * Upload a single file to S3
     */
    const uploadFile = async (
        fileWithStatus: FileWithStatus,
    ): Promise<void> => {
        const { file, id } = fileWithStatus;
        try {
            console.log(
                `Starting upload process for file: ${file.name} (${file.type}, ${file.size} bytes)`,
            );

            // Update file status to uploading
            setFiles((files) =>
                files.map((f) =>
                    f.id === id
                        ? {
                              ...f,
                              status: "uploading",
                              error: undefined,
                          }
                        : f,
                ),
            );

            // Create a new S3Upload instance
            const uploader = new S3Upload({
                // Function to get the signed URL
                getSignedUrl: async (uploadFile, callback) => {
                    try {
                        console.log(
                            `[Upload] Requesting presigned URL for ${uploadFile.name} (type: ${uploadFile.type}, size: ${uploadFile.size} bytes)`,
                        );
                        const response =
                            await documentApiService.generateUploadUrl({
                                fileName: uploadFile.name,
                                contentType: uploadFile.type,
                            });

                        console.log("[Upload] Received presigned URL response");

                        // Make sure we have a valid URL
                        if (!response || !response.url) {
                            console.error(
                                "[Upload] Invalid response:",
                                response,
                            );
                            throw new Error(
                                "Invalid or missing presigned URL in response",
                            );
                        }

                        // Call the callback with the signed URL
                        callback({
                            signedUrl: response.url,
                            url: response.url,
                            contentType: response.contentType,
                        });
                    } catch (error) {
                        console.error(
                            "[Upload] Error getting signed URL:",
                            error,
                        );
                        throw error;
                    }
                },

                // Error callback with proper typing
                onError: (
                    errorMsg: string,
                    //uploadFile: File,
                    context?: unknown,
                ) => {
                    console.error(`Upload error: ${errorMsg}`, context);
                    // Don't throw here, let the Promise rejection handle it
                },
            });

            // Perform the upload using the original File object
            await uploader.uploadFile(file);

            console.log(`Upload completed for ${file.name}`);
            setFiles((files) =>
                files.map((f) =>
                    f.id === id
                        ? {
                              ...f,
                              status: "success",
                              error: undefined,
                          }
                        : f,
                ),
            );
            toast.success(`${file.name} uploaded successfully`);
        } catch (error) {
            console.error("Error in upload process:", error);
            setFiles((files) =>
                files.map((f) =>
                    f.id === id
                        ? {
                              ...f,
                              status: "error",
                              error:
                                  error instanceof Error
                                      ? error.message
                                      : "Unknown error",
                          }
                        : f,
                ),
            );
            toast.error(
                `Failed to upload ${file.name}: ${error instanceof Error ? error.message : "Unknown error"}`,
            );
        }
    };

    /**
     * Upload all pending files sequentially
     */
    const uploadAllFiles = async (): Promise<void> => {
        try {
            const pendingFiles = files.filter((file) => file.status === "idle");

            if (pendingFiles.length === 0) {
                return;
            }

            // Show total size being uploaded
            const totalSize = pendingFiles.reduce(
                (sum, file) => sum + file.file.size,
                0,
            );
            console.log(
                `Uploading ${pendingFiles.length} files (${formatFileSize(totalSize)} total)`,
            );

            // Upload files sequentially to avoid overwhelming the network
            for (const file of pendingFiles) {
                await uploadFile(file);
            }

            toast.success(`Uploaded ${pendingFiles.length} files successfully`);
        } catch (error) {
            console.error("Error in batch upload:", error);
            toast.error("Some files failed to upload");
        }
    };

    /**
     * Get appropriate icon based on file type
     */
    const getFileIcon = (fileWithStatus: FileWithStatus): JSX.Element => {
        if (
            fileWithStatus.file.type &&
            fileWithStatus.file.type.startsWith("image/")
        ) {
            return <Image className="h-4 w-4" aria-label="Image file" />;
        }
        return <FileText className="h-4 w-4 " aria-label="Document file" />;
    };

    return (
        <Card
            elevation={elevation}
            className={`w-full max-w-md shadow-sm flex flex-col ${files.length > 0 ? className || "" : (className || "").replace("h-full", "")}`}
        >
            <CardHeader className="flex-shrink-0">
                <div className="flex flex-row items-center justify-between">
                    <CardTitle>Upload Documents</CardTitle>
                    {onClose && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-md-on-surface-variant hover:text-md-on-surface"
                            onClick={onClose}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                </div>
                {fileStats.total > 0 && (
                    <div className="text-sm text-md-on-surface-variant">
                        {fileStats.total} file{fileStats.total !== 1 ? "s" : ""}{" "}
                        • {formatFileSize(fileStats.totalSize)}
                        {fileStats.completed > 0 && (
                            <span className="text-success">
                                {" "}
                                • {fileStats.completed} completed
                            </span>
                        )}
                        {fileStats.failed > 0 && (
                            <span className="text-md-error">
                                {" "}
                                • {fileStats.failed} failed
                            </span>
                        )}
                    </div>
                )}
            </CardHeader>
            <CardContent
                className={`flex flex-col ${files.length > 0 ? "flex-1 min-h-0" : ""}`}
            >
                <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-200 flex-shrink-0 ${
                        isDragActive
                            ? "border-md-primary bg-md-primary-container/20"
                            : "border-md-outline-variant hover:border-md-primary hover:bg-md-surface-variant/50"
                    }`}
                >
                    <input {...getInputProps()} />
                    <Upload className="h-10 w-10 mx-auto text-md-on-surface-variant" />
                    <p className="mt-2 text-sm text-md-on-surface">
                        {isDragActive
                            ? "Drop the files here..."
                            : "Drag & drop PDF or JPG files here, or click to select"}
                    </p>
                    <p className="text-xs text-md-on-surface-variant mt-1">
                        PDF and JPG files only (max 10MB)
                    </p>
                </div>

                {files.length > 0 && (
                    <>
                        <div className="flex-1 overflow-y-auto mt-4 min-h-0">
                            <div className="space-y-2">
                                {files.map((file) => (
                                    <div
                                        key={file.id}
                                        className={cn(
                                            "flex items-center justify-between p-3  rounded-md bg-md-surface shadow-sm",
                                            `bg-md-surface-level-${elevation + 1}`,
                                        )}
                                    >
                                        <div className="flex items-center space-x-2 overflow-hidden">
                                            {getFileIcon(file)}
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium truncate max-w-[180px]">
                                                    {file.file.name}
                                                </span>
                                                <div className="flex text-xs text-md-on-surface-variant space-x-2">
                                                    <span>
                                                        {formatFileSize(
                                                            file.file.size,
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            {file.status === "uploading" && (
                                                <div className="flex flex-row flex-nowrap items-center">
                                                    <Loader2 className="h-6 w-6 animate-spin text-md-primary" />
                                                    {/* <span className="text-xs text-gray-500">
                                                    Uploading...
                                                </span> */}
                                                </div>
                                            )}
                                            {file.status === "success" && (
                                                <span className="text-xs text-success flex items-center gap-1">
                                                    <Check className="w-3 h-3" />
                                                    Done
                                                </span>
                                            )}
                                            {file.status === "error" && (
                                                <span
                                                    className="text-xs text-md-error flex items-center gap-1"
                                                    title={
                                                        file.error ||
                                                        "Upload failed"
                                                    }
                                                >
                                                    <AlertCircle className="w-3 h-3" />
                                                    Failed
                                                </span>
                                            )}
                                            {file.status !== "uploading" && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6"
                                                    onClick={() =>
                                                        removeFile(file.id)
                                                    }
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-end space-x-2 mt-4 flex-shrink-0">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setFiles([])}
                                disabled={fileStats.uploading > 0}
                            >
                                Clear All
                            </Button>
                            <Button
                                size="sm"
                                onClick={uploadAllFiles}
                                disabled={
                                    fileStats.pending === 0 ||
                                    fileStats.uploading > 0
                                }
                            >
                                Upload{" "}
                                {fileStats.pending > 0
                                    ? fileStats.pending
                                    : "All"}
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
